import { AnalysisReportSections, AnalysisSectionDetails, AnalysisStructure, SectionResult } from '../types';
import { ScoreData, SectionData } from './types';

export function getSectionsProgress(sectionsCount: { passed: number; warning: number; failed: number }, totalSectionsCount: number) {
    return totalSectionsCount !== 0
        ? {
              passed: (sectionsCount.passed * 100) / totalSectionsCount,
              warning: (sectionsCount.warning * 100) / totalSectionsCount,
              failed: (sectionsCount.failed * 100) / totalSectionsCount,
          }
        : {
              passed: 0,
              warning: 0,
              failed: 0,
          };
}

export function getTagDataForPriority(relevance: number) {
    if (relevance > 0 && relevance <= 3) {
        return { color: 'green', label: 'LOW' };
    } else if (relevance > 3 && relevance <= 7) {
        return { color: 'orange', label: 'MEDIUM' };
    } else {
        return { color: 'red', label: 'HIGH' };
    }
}

export function getColorByScore(seoScore: number) {
    if (seoScore < 50) return '#f63842';
    if (seoScore >= 50 && seoScore < 75) return '#ffab00';
    if (seoScore >= 75) return '#34bc6e';
}

export function getStatusByScore(seoScore: number) {
    if (seoScore < 50) return 'failed';
    if (seoScore >= 50 && seoScore < 75) return 'warning';
    if (seoScore >= 75) return 'passed';
}

export function filterObjectByKeys<T>(record: Record<string, T>, allowedKeys: string[]): Record<string, T> {
    return allowedKeys.filter((sectionKey) => sectionKey in record).reduce((object, sectionKey) => ({ ...object, [sectionKey]: record[sectionKey] }), {});
}

export function filterSelectedCategorySections(analysisStructure: AnalysisStructure, sections: Record<string, SectionData | null>) {
    return Object.entries(analysisStructure).reduce((acc, [category, categorySections]) => {
        const sectionsInCategory = categorySections
            .filter((section) => section.key in sections && sections[section.key] !== null)
            .map((section) => section.key);

        if (sectionsInCategory.length > 0) {
            return { ...acc, [category]: sectionsInCategory };
        }
        return acc;
    }, {});
}

export function mapReportSectionsToSectionData(sections: AnalysisReportSections) {
    return Object.keys(sections).reduce((record, sectionKey) => {
        if (sections[sectionKey].result) {
            return { ...record, [sectionKey]: { ...sections[sectionKey], passRate: sections[sectionKey].top_100_pass_rate } };
        } else {
            return record;
        }
    }, {} as Record<string, SectionData>);
}

export function getImportantFixes(sections: Record<string, SectionData | null>) {
    return Object.entries(sections)
        .filter(
            ([_, section]) =>
                section !== null && section.result && 'recommendation' in section.result && section.result.status === 'failed' && section.relevance > 0
        )
        .map(([sectionKey, section]) => ({
            sectionKey: sectionKey,
            recommendation: section!.result.recommendation!,
            relevance: section!.relevance,
        }))
        .sort((a, b) => b.relevance - a.relevance);
}

function addSectionToScore(oldScore: ScoreData, status: string, relevance: number) {
    const newScore = { ...oldScore };

    let modifier: number;

    if (status === 'passed') {
        modifier = 1;
        newScore.passed++;
    } else if (status === 'warning') {
        modifier = 0.5;
        newScore.warning++;
    } else {
        modifier = 0;
        newScore.failed++;
    }

    newScore.score += relevance * modifier;

    return newScore;
}

/**
 * Returns the report score suited for the overview. This means that inexistent sections will be deducted
 * from the total score (we don't have details about the section means we don't include it in the report).
 * @param selectedSections
 * @param sections
 * @returns a report score suited for the report overview
 */
export function getGeneralScoreData(selectedSections: AnalysisSectionDetails[], sections: Record<string, SectionData | null>) {
    let scoreData: ScoreData = {
        score: 0,
        passed: 0,
        warning: 0,
        failed: 0,
    };

    let maxPossiblePoints = selectedSections.reduce((sum, section) => {
        return sum + section.relevance;
    }, 0);

    Object.entries(sections).forEach(([sectionKey, section]) => {
        if (section) {
            scoreData = addSectionToScore(scoreData, section.result.status, section.relevance);
        } else {
            // If a section from a live analysis had a fetch error it will be null. In this case subtract that section score from the max possible score
            const nullSectionRelevance = selectedSections.find((section) => section.key === sectionKey)?.relevance || 0;
            maxPossiblePoints -= nullSectionRelevance;
        }
    });

    scoreData.score = maxPossiblePoints === 0 ? 0 : Math.round((scoreData.score * 100) / maxPossiblePoints);

    // if the report has just 1 tool, the tool has relevance 0 and the status is passed, then the score should be 100
    if (selectedSections.length === 1) {
        const sectionData = selectedSections[0];
        const sectionStatus = sections[sectionData.key]?.result.status;

        if (maxPossiblePoints === 0 && sectionStatus === 'passed') {
            scoreData.score = 100;
        }
    }

    return scoreData;
}

/**
 * Adds all section scores and statuses without deducting failed sections from the maximum possible score.
 * @param sections
 * @returns
 */
export function getCumulatedSectionsScoreData(sections: Record<string, SectionData | null>) {
    let scoreData: ScoreData = {
        score: 0,
        passed: 0,
        warning: 0,
        failed: 0,
    };

    let maxPossiblePoints = 0;

    Object.values(sections).forEach((section) => {
        if (section) {
            maxPossiblePoints += section.relevance;
            scoreData = addSectionToScore(scoreData, section.result.status, section.relevance);
        }
    });

    scoreData.score = maxPossiblePoints === 0 ? 0 : Math.round((scoreData.score * 100) / maxPossiblePoints);

    const sectionKeys = Object.keys(sections);

    // if the report has just 1 tool, the tool has relevance 0 and the status is passed, then the score should be 100
    if (sectionKeys.length === 1) {
        const sectionKey = sectionKeys[0];
        const sectionStatus = sections[sectionKey]?.result.status;

        if (maxPossiblePoints === 0 && sectionStatus === 'passed') {
            scoreData.score = 100;
        }
    }

    return scoreData;
}

/**
 * Calculates the report score suited for the overview for competitors
 * @param competitors
 * @param selectedSections
 * @param competitorSections
 * @returns
 */
export function getCompetitorsGeneralScoreData(
    competitors: string[],
    selectedSections: AnalysisSectionDetails[],
    competitorSections: Record<string, { [competitor: string]: SectionResult | null }>
) {
    let scoreData: Record<string, ScoreData> = competitors.reduce(
        (data, competitor) => ({ ...data, [competitor]: { score: 0, passed: 0, warning: 0, failed: 0 } }),
        {} as Record<string, ScoreData>
    );

    const maxPossiblePoints = selectedSections.reduce((sum, section) => {
        return sum + section.relevance;
    }, 0);

    let competitorsMaxPossiblePoints: Record<string, number> = competitors.reduce(
        (data, competitor) => ({ ...data, [competitor]: maxPossiblePoints }),
        {} as Record<string, number>
    );

    Object.entries(competitorSections).forEach(([sectionKey, results]) => {
        Object.entries(results).forEach(([competitor, result]) => {
            const relevance = selectedSections.find((section) => section.key === sectionKey)?.relevance || 0;

            if (result) {
                scoreData[competitor] = addSectionToScore(scoreData[competitor], result.status, relevance);
            } else {
                competitorsMaxPossiblePoints[competitor] -= relevance;
            }
        });
    });

    for (const competitor in scoreData) {
        const competitorMaxPoints = competitorsMaxPossiblePoints[competitor];

        scoreData[competitor].score = competitorMaxPoints === 0 ? 0 : Math.round((scoreData[competitor].score * 100) / competitorMaxPoints);

        // if the report has just 1 tool, the tool has relevance 0 and the status is passed, then the score should be 100
        if (selectedSections.length === 1) {
            const sectionDetails = selectedSections[0];
            const competitorsSection = competitorSections[sectionDetails.key];

            if (competitorsSection) {
                const sectionStatus = competitorsSection[competitor]?.status;

                if (competitorMaxPoints === 0 && sectionStatus === 'passed') {
                    scoreData[competitor].score = 100;
                }
            }
        }
    }

    return scoreData;
}
